import { useEffect, useState } from 'react';

import { useAppSelector } from '@/hooks/useAppSelector';
import SocketConnection from '@/services/socket-connection';
import TokenService from '@/services/token.service';
import type { SocketConnectionStatus } from '@/types/notification';
import type { Socket } from 'socket.io-client';

type UseSocketOptions = {
    /** When false, no connection is opened (ref-count is unchanged). Default true. */
    enabled?: boolean;
};

/**
 * Shared Socket.IO connection for the app. Safe to call from multiple hooks/components;
 * ref-counting keeps a single underlying socket.
 */
export function useSocket({ enabled = true }: UseSocketOptions = {}) {
    const isLoggedIn = useAppSelector(({ auth }) => auth.isLoggedIn);
    const shouldConnect = enabled && isLoggedIn;
    const [connectionStatus, setConnectionStatus] = useState<SocketConnectionStatus>(SocketConnection.getStatus());
    const [socket, setSocket] = useState<Socket | null>(SocketConnection.getSocket());

    useEffect(() => SocketConnection.subscribe(setConnectionStatus), []);

    useEffect(() => {
        setSocket(SocketConnection.getSocket());
    }, [connectionStatus]);

    useEffect(() => {
        if (!shouldConnect) {
            SocketConnection.disconnect();
            return undefined;
        }

        const accessToken = TokenService.getAccessToken();
        if (!accessToken) {
            return undefined;
        }

        SocketConnection.connect(accessToken);
        setSocket(SocketConnection.getSocket());

        const unsubscribeToken = TokenService.subscribe((token) => {
            SocketConnection.updateToken(token);
        });

        return () => {
            unsubscribeToken();
            SocketConnection.disconnect();
        };
    }, [shouldConnect]);

    return {
        socket,
        isConnected: connectionStatus === 'connected',
        connectionStatus
    };
}
